"use client";

import { useState } from "react";
import Link from "next/link";

type Side = "offend" | "defend";

const SUBMENUS: Record<Side, { label: string; href: string }[]> = {
  offend: [
    { label: "Assessment", href: "/service/offend/web-application" },
    {
      label: "Attack All Areas",
      href: "/service/offend/scenario-based-penetration-test",
    },
  ],
  defend: [
    { label: "Analysis", href: "/service/defend/application-security-analysis" },
    {
      label: "Regulatory Compliance",
      href: "/service/defend/regulatory-compliance",
    },
  ],
};

const RED = "#EC1938";
const TEAL = "#00C7CD";
const NEUTRAL = "#D6D6D6";

/**
 * Submenu metrics, expressed against the stage rather than in fixed px.
 *
 * The corner-bracket frame is positioned in % of the stage (top BRACKET_TOP,
 * height 33.75%), so its bottom edge sits 16.875% of the stage height below
 * that line — which is half as much of the stage WIDTH, since the stage is a
 * 1440×720 (2:1) box. The submenu used to be fixed at 16px/24px type with 32px
 * gap and 32px top padding, which cleared that edge only while the stage was
 * at least ~1133px wide: at 1024px viewport the last item hung 17px below the
 * frame. The label block and the frame share ANCHOR_PCT, so this clearance is
 * unaffected by where the composition sits vertically.
 *
 * cqw = 1% of the stage's inline size (the stage declares containerType), so
 * these track the frame exactly as the Figma 1440 ratios: 32/1440 = 2.2222%,
 * 16/1440 = 1.1111%. The clamps keep the type readable on a narrow stage and
 * pin it at the Figma values once the stage stops growing (max-w 1440).
 */
const SUB_GAP = "clamp(16px, 2.2222cqw, 32px)";

/**
 * Vertical anchor of the whole composition, in % of the STAGE height: the X's
 * crossing point, the crosshair, the connector lines and the label/submenu
 * blocks all hang off this one line.
 *
 * The first Figma pass pinned it at 42.2%, which left the artwork hugging the
 * "Services" title. Measured on a 1376px-wide stage (1440 viewport):
 *   • the art's own box is 124.9% of the stage height, so it overflows the
 *     stage top and bottom — only the ink inside it is visible;
 *   • the visible art (1st–99th percentile of its luminance mass over the page
 *     background) ran y=220→693, i.e. 52px of air under the title and 155px
 *     below the art. Top-heavy by ~100px;
 *   • box-centring it is not enough either: the art's mass centre sits at
 *     413/900 of its own box, above the box centre (450/900), so a
 *     box-centred placement still reads high.
 *
 * Putting the anchor on the stage's mid-line balances it — 106px above the
 * visible art, 101px below — and the ink still ends at 97.4% of the stage, so
 * nothing is clipped by the section's overflow-hidden.
 */
const ANCHOR_PCT = 50;
const ANCHOR = `${ANCHOR_PCT}%`;
/**
 * Art box top. The crossing point sits 47.05% into the 900-unit art box, and
 * that box is 124.9% of the stage height tall → 58.77% of the stage height
 * from its top edge. Offsetting the box by that keeps the crossing on ANCHOR.
 */
const ART_TOP = `${(ANCHOR_PCT - 58.77).toFixed(2)}%`;
/** Corner-bracket frame — 2.9% of the stage below the label line, per Figma. */
const BRACKET_TOP = `${ANCHOR_PCT + 2.9}%`;
const SUB_TYPE = {
  // The Figma ladder (12 / 1.1111cqw / 16px) read too small against the 48px
  // side labels, so every stop is the Figma value +15%: 13.8 / 1.2778 / 18.4.
  // The gap is deliberately NOT scaled with it — the block has to stay inside
  // the corner-bracket frame, and the type alone spends ~6px of the 16–29px of
  // headroom the frame leaves (measured 1024→1920). Re-measure before growing
  // this again.
  fontSize: "clamp(13.8px, 1.2778cqw, 18.4px)",
  // em-relative so line box and tracking scale with the font instead of
  // re-introducing a fixed 24px/0.5px step.
  lineHeight: 1.5,
  letterSpacing: "0.03125em",
} as const;

/**
 * Services landing hero — Figma node 1544-51268 ("Services - Home").
 *
 * One page, three interaction states:
 *  • default      → shattered "X" (outline + red OFFEND arrow + crosshair), gray labels
 *  • OFFEND hover → corner brackets + red label/glow + connector line + offend submenu
 *  • DEFEND hover → composition mirrors (scaleX -1), arrow becomes teal, teal label +
 *                   connector line + defend submenu
 *
 * The "solid diamond" in the design is the dense shattered-fragment cloud, not a fill.
 * Layer geometry follows the Figma 899×899 art group (1767:35340); labels / brackets /
 * lines are positioned against the surrounding 1440-frame.
 */
export default function ServicesHero() {
  const [hovered, setHovered] = useState<Side | null>(null);

  const isDefend = hovered === "defend";

  return (
    <section className="relative w-full overflow-hidden bg-background">
      <div className="relative mx-auto w-full max-w-[1440px] px-4 sm:px-8">
        {/* SERVICES title */}
        <h1 className="relative z-30 pt-[96px] text-center font-bold uppercase text-white select-none md:pt-[120px]">
          <span
            className="font-[family-name:var(--font-avenir)]"
            style={{
              fontSize: "clamp(34px, 3.8vw, 48px)",
              letterSpacing: "0.06em",
              lineHeight: 1,
            }}
          >
            Services
          </span>
        </h1>

        {/* Hero stage — coordinate box mapped to the Figma frame (1440 × 720).
            Hover-driven and calibrated for the 1440 desktop frame, so it's
            hidden below lg (narrower screens get the stacked layout below).

            The switch is `lg`, not `md`: 1024px is the site's one mobile/desktop
            line (the header nav, `useIsDesktop`, and every other section use it),
            and a hover-only composition has no business appearing at 768px where
            the header is still a hamburger and the pointer may not exist. */}
        <div
          className="relative mx-auto -mt-2 hidden aspect-[1440/720] w-full max-w-[1440px] lg:block"
          // Query container for the submenus. Every other layer here is sized in
          // % of the stage, so it shrinks with the frame; the submenus were the
          // only px-sized part and hung below the corner brackets on any stage
          // narrower than ~1133px. cqw ties them to the same box.
          style={{ containerType: "inline-size" }}
          onMouseLeave={() => setHovered(null)}
        >
          {/* Hover zones — left half = OFFEND, right half = DEFEND. Large, stable
              targets sit below the labels (z-[5]) so the whole side is hoverable
              and the cursor never flickers in/out of a tiny text box. */}
          <div
            className="absolute inset-y-0 left-0 z-[5] w-1/2"
            onMouseEnter={() => setHovered("offend")}
          />
          <div
            className="absolute inset-y-0 right-0 z-[5] w-1/2"
            onMouseEnter={() => setHovered("defend")}
          />
          {/* ---------------------------------------------------------------- */}
          {/* X composition — the Figma art (node 1767:35340) exported as ONE   */}
          {/* 900×900 SVG: shattered fragments + X (red blade + solid gray blade */}
          {/* + outline), red for default/OFFEND, teal variant + horizontal      */}
          {/* mirror for DEFEND. The crosshair is a SEPARATE overlay because it  */}
          {/* changes per state (see below).                                     */}
          {/* ---------------------------------------------------------------- */}
          <div
            aria-hidden
            className="pointer-events-none absolute"
            style={{
              // The art's crossing point sits at (50.17%, 47.06%) of the 900
              // box; place the box so that lands on the page centre and on the
              // label line (see ANCHOR / ART_TOP).
              left: "18.65%",
              top: ART_TOP,
              width: "62.45%",
              aspectRatio: "1 / 1",
              transformOrigin: "50.17% 50%",
              transform: isDefend ? "scaleX(-1)" : "none",
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/service/services_x_composition.svg"
              alt=""
              className="absolute inset-0 block h-full w-full"
              style={{ opacity: isDefend ? 0 : 1 }}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/service/services_x_composition_cyan.svg"
              alt=""
              className="absolute inset-0 block h-full w-full"
              style={{ opacity: isDefend ? 1 : 0 }}
            />

            {/* Crosshair overlay — the design uses a DIFFERENT reticle per state
                (Figma nodes 1767:35340 / 1000003108):
                  • default → vertical line, full ring, red centre
                  • OFFEND  → diagonal line along the blade, broken ring, red centre
                  • DEFEND  → same diagonal (mirrored by the container), teal centre
                Kept out of the composition SVG so it can swap cleanly. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/service/services_crosshair_default.svg"
              alt=""
              className="absolute inset-0 block h-full w-full transition-opacity duration-200"
              style={{ opacity: hovered ? 0 : 1 }}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/service/services_crosshair_offend.svg"
              alt=""
              className="absolute inset-0 block h-full w-full transition-opacity duration-200"
              style={{ opacity: hovered === "offend" ? 1 : 0 }}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/service/services_crosshair_defend.svg"
              alt=""
              className="absolute inset-0 block h-full w-full transition-opacity duration-200"
              style={{ opacity: isDefend ? 1 : 0 }}
            />
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* Corner brackets — appear on any hover                            */}
          {/* ---------------------------------------------------------------- */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/service/services_bracket_left.svg"
            alt=""
            aria-hidden
            className="pointer-events-none absolute z-10 transition-opacity duration-300"
            style={{
              left: "5%",
              top: BRACKET_TOP,
              width: "1.74%",
              height: "33.75%",
              transform: "translateY(-50%)",
              opacity: hovered ? 1 : 0,
            }}
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/service/services_bracket_right.svg"
            alt=""
            aria-hidden
            className="pointer-events-none absolute z-10 transition-opacity duration-300"
            style={{
              right: "5%",
              top: BRACKET_TOP,
              width: "1.74%",
              height: "33.75%",
              // Mirror so the corners hook inward (toward centre), matching the
              // left bracket. The exported right asset opens outward otherwise.
              transform: "translateY(-50%) scaleX(-1)",
              opacity: hovered ? 1 : 0,
            }}
          />

          {/* ---------------------------------------------------------------- */}
          {/* Connector lines (label → center crosshair)                       */}
          {/* ---------------------------------------------------------------- */}
          {/* OFFEND line: white, diamond tip at the crosshair end (right) */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/service/services_line_offend.svg"
            alt=""
            aria-hidden
            className="pointer-events-none absolute z-[15]"
            style={{
              left: "12.43%",
              top: ANCHOR,
              width: "30.96%",
              height: "6px",
              transform: "translateY(-50%)",
              opacity: hovered === "offend" ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />
          {/* DEFEND line: white, diamond tip at the crosshair end (left).
              Right edge at 1236/1440 = 14.17% per Figma (Line 3, node 1574:42959). */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/service/services_line_defend.svg"
            alt=""
            aria-hidden
            className="pointer-events-none absolute z-[15]"
            style={{
              right: "14.17%",
              top: ANCHOR,
              width: "30.96%",
              height: "6px",
              transform: "translateY(-50%)",
              opacity: isDefend ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />

          {/* ---------------------------------------------------------------- */}
          {/* OFFEND label + submenu (left)                                    */}
          {/* ---------------------------------------------------------------- */}
          <div
            className="absolute z-20 select-none text-left"
            style={{
              left: hovered === "offend" ? "12.43%" : "19.2%",
              top: ANCHOR,
            }}
            onMouseEnter={() => setHovered("offend")}
          >
            <Link
              href="/service/offend/scenario-based-penetration-test"
              className="absolute bottom-full left-0 mb-[10px] block font-[family-name:var(--font-avenir)] font-semibold uppercase transition-colors duration-200"
              style={{
                fontSize: "clamp(28px, 3.3vw, 48px)",
                lineHeight: 1,
                color: hovered === "offend" ? RED : NEUTRAL,
              }}
            >
              Offend
            </Link>
            <div
              className="flex flex-col"
              style={{
                // Figma 1440 frame: 32px gap / 32px top padding → cqw so the
                // block scales with the bracket frame instead of overflowing it.
                gap: SUB_GAP,
                paddingTop: SUB_GAP,
                opacity: hovered === "offend" ? 1 : 0,
                pointerEvents: hovered === "offend" ? "auto" : "none",
                transition: "opacity 0.2s ease",
              }}
            >
              {SUBMENUS.offend.map((s, i) => (
                <Link
                  key={s.href}
                  href={s.href}
                  className="font-[family-name:var(--font-avenir)] font-semibold uppercase text-white transition-colors hover:text-primary"
                  style={{
                    ...SUB_TYPE,
                    animation:
                      hovered === "offend"
                        ? `svcMenuIn 0.3s ease-out ${i * 80}ms both`
                        : "none",
                  }}
                >
                  {s.label}
                </Link>
              ))}
            </div>
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* DEFEND label + submenu (right)                                   */}
          {/* ---------------------------------------------------------------- */}
          <div
            className="absolute z-20 select-none text-right"
            style={{
              // DEFEND is right-aligned in its Figma frame with a 26px inset, so it
              // sits ~26px further from the right edge than OFFEND is from the left.
              // hover: 1236/1440 = 14.17%; default: 1141/1440 → 20.76% from right.
              right: isDefend ? "14.17%" : "20.76%",
              top: ANCHOR,
            }}
            onMouseEnter={() => setHovered("defend")}
          >
            <Link
              href="/service/defend/application-security-analysis"
              className="absolute bottom-full right-0 mb-[10px] block whitespace-nowrap font-[family-name:var(--font-avenir)] font-semibold uppercase transition-colors duration-200"
              style={{
                fontSize: "clamp(28px, 3.3vw, 48px)",
                lineHeight: 1,
                color: isDefend ? TEAL : NEUTRAL,
              }}
            >
              Defend
            </Link>
            <div
              className="flex flex-col items-end"
              style={{
                gap: SUB_GAP,
                paddingTop: SUB_GAP,
                opacity: isDefend ? 1 : 0,
                pointerEvents: isDefend ? "auto" : "none",
                transition: "opacity 0.2s ease",
              }}
            >
              {SUBMENUS.defend.map((s, i) => (
                <Link
                  key={s.href}
                  href={s.href}
                  className="font-[family-name:var(--font-avenir)] font-semibold uppercase text-white transition-colors hover:text-[#00C7CD]"
                  style={{
                    ...SUB_TYPE,
                    animation: isDefend
                      ? `svcMenuIn 0.3s ease-out ${i * 80}ms both`
                      : "none",
                  }}
                >
                  {s.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* MOBILE layout (below lg) — the desktop hover composition crams     */}
        {/* OFFEND / DEFEND onto the centre X on narrow screens and needs a    */}
        {/* pointer, so phones get a dedicated stacked, tap-friendly layout:   */}
        {/* a small X, then each side with its sub-links shown inline.         */}
        {/* ---------------------------------------------------------------- */}
        {/* Tighter rhythm on phones; sm: restores the 640–767px look unchanged. */}
        <div className="flex flex-col items-center gap-8 sm:gap-10 px-4 pb-12 sm:pb-20 pt-6 lg:hidden">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/service/services_x_composition.svg"
            alt=""
            aria-hidden
            className="pointer-events-none w-[190px] max-w-[55%]"
          />

          <div className="flex w-full max-w-[360px] flex-col gap-9">
            {/* OFFEND */}
            <div className="flex flex-col items-center gap-4 text-center">
              <Link
                href="/service/offend/scenario-based-penetration-test"
                className="font-[family-name:var(--font-avenir)] text-[24px] sm:text-[30px] font-semibold uppercase leading-none"
                style={{ color: RED }}
              >
                Offend
              </Link>
              <div className="flex flex-col items-center gap-3">
                {SUBMENUS.offend.map((s) => (
                  <Link
                    key={s.href}
                    href={s.href}
                    className="font-[family-name:var(--font-avenir)] text-[16px] font-semibold uppercase leading-6 tracking-[0.5px] text-white transition-colors hover:text-primary"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="h-px w-full bg-white/10" />

            {/* DEFEND */}
            <div className="flex flex-col items-center gap-4 text-center">
              <Link
                href="/service/defend/application-security-analysis"
                className="font-[family-name:var(--font-avenir)] text-[24px] sm:text-[30px] font-semibold uppercase leading-none"
                style={{ color: TEAL }}
              >
                Defend
              </Link>
              <div className="flex flex-col items-center gap-3">
                {SUBMENUS.defend.map((s) => (
                  <Link
                    key={s.href}
                    href={s.href}
                    className="font-[family-name:var(--font-avenir)] text-[16px] font-semibold uppercase leading-6 tracking-[0.5px] text-white transition-colors hover:text-[#00C7CD]"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes svcMenuIn {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
